import "dockview-core/dist/styles/dockview.css";
import "./ui/styles.css";
import { registerSW } from "virtual:pwa-register";

import { DockviewComponent, type IContentRenderer, type IDockviewPanel } from "dockview-core";

import { mountLayout } from "./ui/layout";
import { buildPrintVariant } from "./core/print";
import { createEditor, languageFor, type EditorHandle } from "./ui/editor";
import { PreviewPane } from "./ui/preview";
import { createFileTree } from "./ui/file-tree";

import { getPandoc } from "./core/pandoc";
import { renderDeck } from "./core/render";

import { seedIfEmpty, topUpBundledThemes } from "./storage/seed";
import {
  buildRenderInputs,
  getActiveEditor,
  getActiveQmd,
  isTextFile,
  listQmds,
  readFile,
  saveFile,
  setActiveEditor,
  setActiveQmd,
} from "./storage/project";
import { exportZip, importZip } from "./storage/zip";
import {
  exists,
  listFiles,
  probeCapabilities,
  remove,
  rename,
  writeBytes,
  writeText,
} from "./storage/storage";
import { readFileBytes, saveImageToAssets } from "./core/image-insert";
import { rebaseChildPath } from "./core/path-resolve";

const AUTOSAVE_MS = 2_000;

// One per file open in the editor area. Each tab is its own Dockview panel
// containing a fresh CodeMirror instance — so each file has its own undo
// history, vim mode state, scroll position, and autosave timer. The
// `componentId` is a stable handle Dockview uses; `path` is the (mutable on
// rename) project path the editor reads from / writes to.
interface OpenEditor {
  path: string;
  componentId: string;
  handle: EditorHandle;
  container: HTMLElement;
  panel: IDockviewPanel;
  autosaveTimer: ReturnType<typeof setTimeout> | null;
  pendingSave: Promise<void> | null;
}

async function main(): Promise<void> {
  const appEl = document.getElementById("app")!;
  const layout = mountLayout(appEl);

  // iPad keyboard avoidance. When the on-screen keyboard — or, with a
  // hardware keyboard attached, the accessory/shortcuts bar — appears, iOS
  // shrinks the visual viewport and shifts the layout up, riding the
  // toolbar off the top of the screen. Pin #app to exactly cover the
  // visual viewport: height tracks visualViewport.height so the lost space
  // comes off the bottom, and the transform re-anchors the top edge to the
  // visible area instead of letting it scroll past the top of the screen.
  const vv = window.visualViewport;
  if (vv) {
    const syncViewport = () => {
      appEl.style.height = `${vv.height}px`;
      appEl.style.transform = `translate(${vv.offsetLeft}px, ${vv.offsetTop}px)`;
    };
    vv.addEventListener("resize", syncViewport);
    vv.addEventListener("scroll", syncViewport);
    syncViewport();
  }

  // Vim bindings default on (per spec) but can be toggled off — handy on a
  // phone. The choice persists across reloads and applies to every open tab.
  const VIM_KEY = "slipway:vim";
  let vimEnabled = localStorage.getItem(VIM_KEY) !== "off";

  if (navigator.storage?.persist) void navigator.storage.persist();

  // Register the service worker generated by vite-plugin-pwa.
  //
  // onOfflineReady  — first install complete; all assets cached → green plane.
  // onNeedRefresh   — a new SW version is waiting to activate → amber button.
  //                   Calling updateSW(true) triggers skipWaiting + reload.
  //
  // onOfflineReady only fires on the very first install. On every subsequent
  // visit (including every PWA launch from the Home Screen) the SW is already
  // controlling the page when it loads, so the callback is never called. We
  // check navigator.serviceWorker.controller at startup to cover that case.
  const updateSW = registerSW({
    onOfflineReady() {
      layout.setOfflineReady(true);
    },
    onNeedRefresh() {
      layout.showUpdateReady(() => void updateSW(true));
    },
  });

  if (navigator.serviceWorker?.controller) {
    layout.setOfflineReady(true);
  }

  layout.setStatus("Seeding project…");
  let wasSeeded = false;
  try {
    wasSeeded = await seedIfEmpty();
    // Existing installs (seeded before a theme shipped) get the missing
    // bundled themes + fonts topped up here, non-destructively.
    if (!wasSeeded) {
      const added = await topUpBundledThemes();
      if (added.length > 0) {
        layout.setStatus(`Added bundled themes (${added.length} files)`);
      }
    }
  } catch (e) {
    layout.setStatus(`Seed failed: ${msgOf(e)}`, "error");
    console.error("seed failed:", e);
    throw e;
  }
  if (wasSeeded) layout.setStatus("Seeded with imago workshop template");

  // ---- Dock + per-file editor state ---------------------------------------

  const previewContainer = document.createElement("div");
  previewContainer.style.cssText = "height:100%;width:100%;position:relative";
  const filesContainer = document.createElement("div");
  filesContainer.style.cssText = "height:100%;width:100%";

  const previewPane = new PreviewPane(previewContainer);
  let lastRenderedHtml: string | null = null;

  // Open editors keyed by current path. Each one also pushes a renderer
  // into `containerRenderers` under its `componentId` so Dockview can
  // find the right container in the createComponent factory.
  const opens = new Map<string, OpenEditor>();
  const containerRenderers: Record<string, IContentRenderer> = {
    files: { element: filesContainer, init: () => {} },
    preview: { element: previewContainer, init: () => {} },
  };
  let nextEditorId = 1;

  const dock = new DockviewComponent(layout.paneHost, {
    createComponent: (options) => {
      const renderer = containerRenderers[options.name];
      if (!renderer) throw new Error(`unknown dock component: ${options.name}`);
      return renderer;
    },
  });
  // Default layout: editor left, preview top-right, files bottom-right.
  // Preview claims the whole pane first. Files is added AFTER the first editor
  // splits the layout into two columns, so it docks below just the right column.
  dock.addPanel({ id: "preview", component: "preview", title: "Preview" });
  let filesAdded = false;
  const resizeDock = () => dock.layout(layout.paneHost.clientWidth, layout.paneHost.clientHeight);
  resizeDock();
  // Re-layout on any pane-host size change: window resize, splitter drags,
  // and the #app height change when the iPad keyboard opens or closes.
  new ResizeObserver(resizeDock).observe(layout.paneHost);

  // Dock-level events dispatch to whichever OpenEditor owns the panel id.
  // Doing it here (rather than per-panel) keeps the lifecycle in one place
  // and dodges Dockview's per-panel api not exposing a dispose hook.
  dock.onDidActivePanelChange((panel) => {
    if (!panel) return;
    const open = findOpenByComponentId(panel.id);
    if (!open) return;
    setActiveEditor(open.path);
    if (open.path.toLowerCase().endsWith(".qmd")) setActiveQmd(open.path);
    fileTree.setActive(open.path);
  });
  dock.onDidRemovePanel((panel) => {
    const open = findOpenByComponentId(panel.id);
    if (!open) return;
    if (open.autosaveTimer) clearTimeout(open.autosaveTimer);
    // Best-effort flush so a close right after typing doesn't lose
    // the last few keystrokes.
    void saveFile(open.path, open.handle.getDoc()).catch(() => {});
    opens.delete(open.path);
    delete containerRenderers[open.componentId];
    if (getActiveEditor() === open.path) {
      const next = opens.values().next().value as OpenEditor | undefined;
      setActiveEditor(next?.path ?? null);
      if (!next) fileTree.setActive(null);
    }
  });

  function findOpenByComponentId(id: string): OpenEditor | null {
    for (const o of opens.values()) if (o.componentId === id) return o;
    return null;
  }

  // ---- File tree wiring ----------------------------------------------------

  // Move/rename a single file in storage and keep in-memory state in sync: its
  // open editor tab (if any) and the active-editor / active-deck pointers.
  async function moveOne(from: string, to: string): Promise<void> {
    const open = opens.get(from);
    if (open) await flushAutosave(open);
    await rename(from, to);
    if (open) {
      opens.delete(from);
      open.path = to;
      opens.set(to, open);
      open.panel.api.setTitle(to);
    }
    if (getActiveEditor() === from) setActiveEditor(to);
    if (getActiveQmd() === from) setActiveQmd(to);
  }

  const fileTree = createFileTree(filesContainer, {
    onOpen: (path) => void openFile(path),
    onRename: async (oldPath, newPath) => {
      try {
        // A file has an exact entry; a "folder" is only an implicit prefix
        // over its descendants, so moving one means re-keying every child.
        if (await exists(oldPath)) {
          await moveOne(oldPath, newPath);
          await refreshTree();
          layout.setStatus(`Moved → ${newPath}`, "ok");
          return;
        }
        const children = await listFiles(oldPath + "/");
        if (children.length === 0) {
          layout.setStatus(`Nothing to move at ${oldPath}`, "warn");
          return;
        }
        // Pre-check collisions across the whole subtree before touching anything.
        const moves = children.map((c) => [c, rebaseChildPath(oldPath, newPath, c)] as const);
        for (const [, dest] of moves) {
          if (await exists(dest)) throw new Error(`Target already exists: ${dest}`);
        }
        for (const [src, dest] of moves) await moveOne(src, dest);
        await refreshTree();
        layout.setStatus(`Moved ${moves.length} files → ${newPath}/`, "ok");
      } catch (e) {
        layout.setStatus(`Move failed: ${msgOf(e)}`, "error");
      }
    },
    onDelete: async (path) => {
      try {
        // Close any open tab for this file first (the panel's onWillRemove
        // handler keeps `opens` in sync). Then delete from storage.
        const open = opens.get(path);
        if (open) dock.removePanel(open.panel);
        await remove(path);
        if (getActiveQmd() === path) {
          const remainingQmds = (await listQmds()).filter((p) => p !== path);
          setActiveQmd(remainingQmds[0] ?? null);
        }
        await refreshTree();
        layout.setStatus(`Deleted ${path}`, "ok");
      } catch (e) {
        layout.setStatus(`Delete failed: ${msgOf(e)}`, "error");
      }
    },
    onCreateFile: async (parentDir, name) => {
      const path = joinPath(parentDir, name);
      try {
        if (await exists(path)) {
          layout.setStatus(`File already exists: ${path}`, "warn");
          return;
        }
        await writeText(path, "");
        await refreshTree();
        if (isTextFile(path)) await openFile(path);
        layout.setStatus(`Created ${path}`, "ok");
      } catch (e) {
        layout.setStatus(`Create failed: ${msgOf(e)}`, "error");
      }
    },
    onCreateFolder: async (parentDir, name) => {
      // IDB has no real directories — model empty folders with a .placeholder
      // stub that the tree filter hides.
      const placeholder = `${joinPath(parentDir, name)}/.placeholder`;
      try {
        await writeText(placeholder, "");
        await refreshTree();
        layout.setStatus(`Created folder ${joinPath(parentDir, name)}`, "ok");
      } catch (e) {
        layout.setStatus(`Create folder failed: ${msgOf(e)}`, "error");
      }
    },
    onImageFile: (file) => void handleImageFile(file),
    onDropFiles: async (files, targetDir) => {
      layout.setStatus(`Saving ${files.length} file${files.length === 1 ? "" : "s"}…`);
      const saved: string[] = [];
      const errors: string[] = [];
      for (const file of files) {
        const path = joinPath(targetDir, file.name);
        try {
          const bytes = await readFileBytes(file);
          await writeBytes(path, bytes);
          saved.push(path);
        } catch (e) {
          errors.push(`${file.name}: ${msgOf(e)}`);
        }
      }
      await refreshTree();
      if (saved[0]) fileTree.reveal(saved[0]);
      if (errors.length) {
        layout.setStatus(`Saved ${saved.length}, failed: ${errors.join("; ")}`, "error");
      } else {
        layout.setStatus(`Added ${saved.join(", ")}`, "ok");
      }
    },
  });

  // ---- Editor lifecycle ----------------------------------------------------

  async function openFile(path: string): Promise<void> {
    if (!isTextFile(path)) {
      layout.setStatus(`${path} isn't a text file — open skipped`, "warn");
      return;
    }
    const existing = opens.get(path);
    if (existing) {
      existing.panel.api.setActive();
      return;
    }
    const componentId = `editor-${nextEditorId++}`;
    const container = document.createElement("div");
    container.style.cssText = "height:100%;width:100%;display:flex;flex-direction:column;min-height:0";
    containerRenderers[componentId] = { element: container, init: () => {} };

    const open: OpenEditor = {
      path,
      componentId,
      container,
      handle: undefined as unknown as EditorHandle,
      panel: undefined as unknown as IDockviewPanel,
      autosaveTimer: null,
      pendingSave: null,
    };
    open.handle = createEditor({
      parent: container,
      initialDoc: await readFile(path),
      language: languageFor(path),
      vimEnabled,
      onChange: () => {
        if (open.path.toLowerCase().endsWith(".qmd")) layout.setStale(true);
        scheduleAutosave(open);
      },
      onSave: () => void manualSave(open),
      onRender: () => void runRender(),
      onImageFile: (file) => void handleImageFile(file, open),
    });

    // First editor opens to the left of Preview (so the right-hand column
    // ends up Preview on top, Files on bottom); subsequent ones stack as
    // tabs in the first editor's group, wherever the user has dragged it.
    const anyExisting = opens.values().next().value as OpenEditor | undefined;
    const position = anyExisting
      ? { referenceGroup: anyExisting.panel.group }
      : { referencePanel: "preview", direction: "left" as const };
    open.panel = dock.addPanel({
      id: componentId,
      component: componentId,
      title: path,
      position,
    });
    // First editor split: now that the editor occupies the left and preview
    // the top-right, add files below preview so it only covers the right column.
    if (!anyExisting && !filesAdded) {
      filesAdded = true;
      const fp = dock.addPanel({
        id: "files",
        component: "files",
        title: "Files",
        position: { referencePanel: "preview", direction: "below" },
      });
      fp.api.setSize({ height: 240 });
    }
    // Active-change + removal are wired at dock-level above; nothing to hook
    // per-panel here.
    opens.set(path, open);
    setActiveEditor(path);
    if (path.toLowerCase().endsWith(".qmd")) {
      setActiveQmd(path);
      layout.setStale(true);
    }
    fileTree.setActive(path);
    layout.setStatus(`Opened ${path}`);
  }

  async function refreshTree(): Promise<void> {
    // Pass the full list; buildTree hides dot-prefixed leaves (folder
    // .placeholder stubs, .seeded / .bundled-themes markers) while keeping the
    // directories they establish — so empty folders still show up.
    fileTree.refresh(await listFiles());
    fileTree.setActive(getActiveEditor());
  }

  await refreshTree();

  // Open a deck at boot so the user lands in a familiar place. Prefer the
  // getting-started deck (slide.qmd, wherever it lives) over whatever sorts
  // first alphabetically — e.g. demos/imago-showcase.qmd would otherwise win.
  const initialQmds = await listQmds();
  const initialQmd = initialQmds.find((p) => p.split("/").pop() === "slide.qmd") ?? initialQmds[0];
  if (initialQmd) {
    await openFile(initialQmd);
  } else {
    layout.setStatus("No .qmd files in project — import a zip or create one", "warn");
  }

  // ---- Toolbar wiring ------------------------------------------------------

  layout.renderBtn.addEventListener("click", () => void runRender());

  layout.importInput.addEventListener("change", async () => {
    const file = layout.importInput.files?.[0];
    if (!file) return;
    layout.setStatus(`Importing ${file.name}…`);
    try {
      // Close every open tab — the files behind them are about to be wiped.
      for (const open of [...opens.values()]) dock.removePanel(open.panel);
      const { filesWritten } = await importZip(file);
      layout.setStatus(`Imported ${filesWritten} files`, "ok");
      const qmds = await listQmds();
      if (qmds[0]) {
        await openFile(qmds[0]);
      } else {
        setActiveQmd(null);
        setActiveEditor(null);
        layout.setStatus("Imported zip contains no .qmd files", "warn");
      }
      await refreshTree();
    } catch (e) {
      layout.setStatus(`Import failed: ${msgOf(e)}`, "error");
    } finally {
      layout.importInput.value = "";
    }
  });

  // ---- Image insertion (file picker, paste, drag-drop) --------------------

  async function handleImageFile(file: File | Blob, targetOpen?: OpenEditor): Promise<void> {
    const active = targetOpen ?? [...opens.values()].find((o) => o.path === getActiveEditor());
    try {
      layout.setStatus("Saving image…");
      const bytes = await readFileBytes(file);
      const path = await saveImageToAssets(bytes, file.type || "image/png");
      await refreshTree();
      fileTree.reveal(path);
      if (active) {
        active.handle.insertImageMarkdown(path);
        if (active.path.toLowerCase().endsWith(".qmd")) layout.setStale(true);
      }
      layout.setStatus(`Inserted ${path}`, "ok");
    } catch (e) {
      layout.setStatus(`Image insert failed: ${msgOf(e)}`, "error");
    }
  }

  layout.exportBtn.addEventListener("click", async () => {
    await flushAllAutosaves();
    layout.setStatus("Exporting…");
    try {
      const blob = await exportZip(lastRenderedHtml);
      downloadBlob(blob, suggestedExportName());
      const note = lastRenderedHtml ? " + rendered/index.html" : "";
      layout.setStatus(`Exported (${(blob.size / 1024).toFixed(0)} KB${note})`, "ok");
    } catch (e) {
      layout.setStatus(`Export failed: ${msgOf(e)}`, "error");
    }
  });

  layout.presentBtn.addEventListener("click", () => {
    if (lastRenderedHtml) previewPane.openInNewTab(lastRenderedHtml);
  });

  layout.pdfBtn.addEventListener("click", () => {
    if (!lastRenderedHtml) return;
    previewPane.openInNewTab(buildPrintVariant(lastRenderedHtml));
    layout.setStatus("Print view opened — in Safari's sheet, pick US Letter + Landscape", "ok");
  });

  layout.setVimOn(vimEnabled);
  layout.vimBtn.addEventListener("click", () => {
    vimEnabled = !vimEnabled;
    localStorage.setItem(VIM_KEY, vimEnabled ? "on" : "off");
    for (const o of opens.values()) o.handle.setVim(vimEnabled);
    layout.setVimOn(vimEnabled);
  });

  // ---- Pandoc loading ------------------------------------------------------

  layout.setStatus("Loading pandoc.wasm…");
  void getPandoc((loaded, total) => {
    if (total > 0) {
      layout.setStatus(`pandoc.wasm: ${(loaded / 1_048_576).toFixed(1)} / ${(total / 1_048_576).toFixed(1)} MB`);
    } else {
      layout.setStatus(`pandoc.wasm: ${(loaded / 1_048_576).toFixed(1)} MB`);
    }
  })
    .then(() => layout.setStatus("Ready", "ok"))
    .catch((e) => layout.setStatus(`pandoc load failed: ${msgOf(e)}`, "error"));

  // ---- Render --------------------------------------------------------------

  async function runRender(): Promise<void> {
    const path = getActiveQmd();
    if (!path) return;
    await flushAllAutosaves();
    layout.renderBtn.disabled = true;
    layout.setStatus("Rendering…");
    // Clear any previous run's warnings affordance while the new render runs.
    layout.setStatusDetails([]);
    try {
      const pandoc = await getPandoc();
      const inputs = await buildRenderInputs(path);
      const result = await renderDeck(pandoc, inputs);
      previewPane.render(result.html);
      lastRenderedHtml = result.html;
      layout.presentBtn.disabled = false;
      layout.pdfBtn.disabled = false;
      layout.setStale(false);
      const warn = result.warnings.length ? ` (${result.warnings.length} warnings)` : "";
      layout.setStatus(`Rendered in ${Math.round(result.durationMs)}ms${warn}`, "ok");
      layout.setStatusDetails(result.warnings);
      if (result.warnings.length) {
        layout.status.title = result.warnings.join("\n");
        console.warn("render warnings:\n" + result.warnings.join("\n"));
      } else {
        layout.status.title = "";
      }
      if (result.stderr.trim()) console.warn("render stderr:", result.stderr);
    } catch (e) {
      layout.setStatus(`Render failed: ${msgOf(e)}`, "error");
      console.error(e);
    } finally {
      layout.renderBtn.disabled = false;
    }
  }

  // ---- Autosave (per-editor) ----------------------------------------------

  function scheduleAutosave(open: OpenEditor): void {
    if (open.autosaveTimer) clearTimeout(open.autosaveTimer);
    open.autosaveTimer = setTimeout(() => {
      open.autosaveTimer = null;
      open.pendingSave = saveFile(open.path, open.handle.getDoc());
    }, AUTOSAVE_MS);
  }

  async function flushAutosave(open: OpenEditor): Promise<void> {
    if (open.autosaveTimer) {
      clearTimeout(open.autosaveTimer);
      open.autosaveTimer = null;
      open.pendingSave = saveFile(open.path, open.handle.getDoc());
    }
    if (open.pendingSave) await open.pendingSave;
  }

  async function flushAllAutosaves(): Promise<void> {
    await Promise.all([...opens.values()].map(flushAutosave));
  }

  async function manualSave(open: OpenEditor): Promise<void> {
    await flushAutosave(open);
    await saveFile(open.path, open.handle.getDoc());
    layout.setStatus(`Saved ${open.path}`, "ok");
  }
}

// ---- Helpers ---------------------------------------------------------------

function joinPath(parentDir: string, name: string): string {
  const safeName = name.replace(/^\/+/, "").replace(/\/+/g, "/");
  if (!parentDir) return safeName;
  return `${parentDir.replace(/\/+$/, "")}/${safeName}`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function suggestedExportName(): string {
  const t = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `slipway-${t.getFullYear()}${pad(t.getMonth() + 1)}${pad(t.getDate())}-${pad(t.getHours())}${pad(t.getMinutes())}.zip`;
}

function msgOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

main().catch(async (e) => {
  console.error("Slipway startup failed:", e);
  await showStartupError(e);
});

async function showStartupError(e: unknown): Promise<void> {
  const message = e instanceof Error ? e.message : String(e);
  const stack = e instanceof Error && e.stack ? e.stack : "";
  let diag = "";
  try {
    const cap = await probeCapabilities();
    diag =
      `userAgent: ${navigator.userAgent}\n` +
      `indexedDB: ${cap.indexedDB}\n` +
      `writable: ${cap.writable}\n` +
      `persisted: ${cap.persisted}\n` +
      `probeError: ${cap.probeError ?? "(none)"}`;
  } catch (probeErr) {
    diag = `(capability probe also failed: ${probeErr instanceof Error ? probeErr.message : String(probeErr)})`;
  }

  const banner = document.createElement("div");
  banner.className = "slipway-error-banner";
  banner.innerHTML = `
    <div class="head">
      <strong>Slipway failed to start</strong>
      <button class="copy" type="button">Copy details</button>
      <button class="dismiss" type="button" aria-label="Dismiss">×</button>
    </div>
    <p class="msg"></p>
    <details>
      <summary>Diagnostics</summary>
      <pre class="diag"></pre>
      <pre class="stack"></pre>
    </details>`;
  banner.querySelector(".msg")!.textContent = message;
  banner.querySelector(".diag")!.textContent = diag;
  banner.querySelector(".stack")!.textContent = stack;
  banner.querySelector(".dismiss")!.addEventListener("click", () => banner.remove());
  banner.querySelector(".copy")!.addEventListener("click", async () => {
    const text = `Slipway startup failure\n\n${message}\n\n--- diagnostics ---\n${diag}\n\n--- stack ---\n${stack}`;
    try {
      await navigator.clipboard.writeText(text);
      (banner.querySelector(".copy") as HTMLButtonElement).textContent = "Copied";
    } catch {
      const range = document.createRange();
      range.selectNodeContents(banner.querySelector(".diag")!);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  });
  document.body.insertBefore(banner, document.body.firstChild);

  const status = document.querySelector(".slipway-toolbar .status");
  if (status) {
    status.textContent = "Startup failed (see banner)";
    status.className = "status error";
  }
}
